import { convertBigInt } from '@core/logic/helper/number';
import { errorBox, infoBox, successBox } from '@core/components/AlertBox/AlertBox';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { XOutline } from '@deepup/icons';
import {
  setSelectedPhoto,
  setSelectedPhotos,
  reduceSelectedPhotos,
  photosState,
  setPhotoSelectionBoundingBox,
} from '@photos/components/PhotosInViewport/state';
import {
  OrderNumber,
  OrderNumberType,
  mapNetworkNodeTypeToOrderNumberSubType,
  isOrderNumberClosed,
} from '@projects/types/orderNumbers';
import type { Photo } from '@photos/types';
import { ExportTarget } from '@projects/types/project';
import { viewer3dSelectors } from '@viewer3D/redux/selectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useSMANumbers,
  useUpdatePhotosWithSmaQuery,
  useUpdateScansWithSmaQuery,
} from '@core/logic/hooks/useSMANumbers';
import { useSnapshot } from 'valtio';
import type { Scan } from '@scans/types';
import {
  uncheckAllScans,
  reduceCheckedScans,
  checkboxSelectionState,
} from '../menu/ScanOrganization/ScansInScopeList/checkboxSelectionState';
import AddSMADialog from './AddSMADialog';
import { useGetSMAMappings, useSaveSMA } from './hooks';

interface SMABulkDialogProps {
  scansIds: string[];
  photosIds: string[];
  title: string;
}

type AssignmentError = {
  asset: 'photo' | 'scan';
  id: string;
};

const dedupeOrderNumbers = (orderNumbers: OrderNumber[]): OrderNumber[] =>
  orderNumbers.filter((orderNumber, index, self) => {
    const firstIndex = self.findIndex((candidate) => candidate.value === orderNumber.value);
    return firstIndex === index;
  });

export const buildCopiedIdsPayload = (scansIds: string[], photosIds: string[]): string => {
  const lines: string[] = [];

  if (scansIds.length > 0) {
    lines.push('ScanIDs:');
    lines.push(...scansIds);
  }

  if (photosIds.length > 0) {
    if (lines.length > 0) {
      lines.push('');
    }
    lines.push('PhotoIDs:');
    lines.push(...photosIds);
  }

  return lines.join('\n');
};

const SMABulkDialog: React.FC<SMABulkDialogProps> = ({ scansIds, photosIds, title }) => {
  const project = useSelector(viewer3dSelectors.selectedProject);
  const { fetchSMANumbers } = useSMANumbers();
  const [open, setOpen] = useState(false);
  const [selectedOrderNumbers, setSelectedOrderNumbers] = useState<string[]>([]);
  const [showNewInput, setShowNewInput] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [newSMAs, setNewSMAs] = useState<OrderNumber[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [assignmentErrors, setAssignmentErrors] = useState<AssignmentError[]>([]);

  const { checkedScans } = useSnapshot(checkboxSelectionState);
  const { selectedPhotos } = useSnapshot(photosState);

  const { data: scanWithSmaData } = useUpdateScansWithSmaQuery(
    {
      projectId: project?.id,
      scans: convertBigInt(checkedScans as Scan[]),
    },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: photoWithSmaData } = useUpdatePhotosWithSmaQuery(
    {
      projectId: project?.id,
      photos: convertBigInt(selectedPhotos as Photo[]),
    },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    },
  );

  const { saveSMA } = useSaveSMA({
    onSuccess: () => setProgress((current) => current + 1),
    projectId: project?.id ?? '',
  });

  const checkedScansWithSma = scanWithSmaData ?? checkedScans;
  const checkedPhotos = photoWithSmaData ?? selectedPhotos;

  const allSMAs = dedupeOrderNumbers([
    ...checkedScansWithSma.flatMap((scan) => (scan.orderNumbers ?? []) as OrderNumber[]),
    ...checkedPhotos.flatMap((photo) => (photo.orderNumbers ?? []) as OrderNumber[]),
    ...newSMAs,
  ]);

  const { getSmaLabel, mappings } = useGetSMAMappings();

  useEffect(() => {
    if (!open) {
      setNewSMAs([]);
      setSelectedOrderNumbers([]);
      setShowNewInput(false);
    }
  }, [open]);

  const onClose = () => {
    setOpen(false);
  };

  const handleNewSMA = (newSMA: OrderNumber) => {
    if (allSMAs.some((sma) => sma.value === newSMA.value)) {
      infoBox(`${newSMA.value} already present in SMA list.`);
      return;
    }
    setNewSMAs((prev) => [...prev, newSMA]);
    setSelectedOrderNumbers((prev) => [...prev, newSMA.value]);
  };

  const getScanOrderNumbers = async (scanId: string): Promise<OrderNumber[]> => {
    const scan = checkedScansWithSma.find(({ id }) => id === scanId);
    if (!scan) {
      return [];
    }
    const orderNumbers = (scan.orderNumbers ?? []) as OrderNumber[];

    if (orderNumbers.length <= 0) {
      if (!project?.id) {
        return [];
      }

      return await fetchSMANumbers(project.id, scan.id, 'scans');
    }

    return orderNumbers;
  };

  const getPhotoOrderNumbers = (photoId: string): OrderNumber[] => {
    const photo = checkedPhotos.find(({ id }) => id === photoId);
    if (!photo) {
      return [];
    }
    return (photo.orderNumbers ?? []) as OrderNumber[];
  };

  const handleAssignmentResults = (
    scansResults: PromiseSettledResult<unknown>[],
    photosResults: PromiseSettledResult<unknown>[],
  ) => {
    const errors: AssignmentError[] = [];

    scansResults.forEach((result, i) => {
      if (result.status === 'rejected') {
        errors.push({ asset: 'scan', id: scansIds[i] });
      }
    });

    photosResults.forEach((result, i) => {
      if (result.status === 'rejected') {
        errors.push({ asset: 'photo', id: photosIds[i] });
      }
    });

    if (errors.length > 0) {
      setAssignmentErrors(errors);
      infoBox('Some SMAs failed to be updated');
    } else {
      successBox('SMAs updated successfully');
      setSelectedPhoto(null);
      setSelectedPhotos([]);
      uncheckAllScans();
      onClose();
    }
  };

  const saveBulkSMAs = async (smas: OrderNumber[] | null) => {
    setIsLoading(true);
    setProgress(0);
    try {
      const scansResults = await Promise.allSettled(
        scansIds.map(async (entityId) => {
          await saveSMA({
            orderNumbers: smas,
            entityId,
            entityType: 'scans',
          });
          if (smas !== null) {
            const textSmaList = smas.map((sma) => sma.value).join(', ');
            const msg = `SMA numbers ${textSmaList} assigned to scan ${entityId} successfully.`;
            successBox(msg);
          }
        }),
      );

      const photosResults = await Promise.allSettled(
        photosIds.map(async (entityId) => {
          await saveSMA({
            orderNumbers: smas,
            entityId,
            entityType: 'photos',
          });
          if (smas !== null) {
            const textSmaList = smas.map((sma) => sma.value).join(', ');
            const msg = `SMA numbers ${textSmaList} assigned to photo ${entityId} successfully.`;
            successBox(msg);
          }
        }),
      );

      handleAssignmentResults(scansResults, photosResults);
    } catch (_error) {
      errorBox('Failed to save changes');
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleCheckboxChange = (value: string) => {
    setSelectedOrderNumbers((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value],
    );
  };

  const handleOverrideWithSelected = () => {
    if (selectedOrderNumbers.length === 0 && !showNewInput) {
      saveBulkSMAs(null);
      return;
    }

    const smasToSave: OrderNumber[] = [];

    newSMAs.forEach((sma) => {
      smasToSave.push(sma);
    });

    selectedOrderNumbers.forEach((sma) => {
      const originalType =
        newSMAs.find((n) => n.value === sma)?.subType ||
        mapNetworkNodeTypeToOrderNumberSubType(mappings[sma]?.at(0)?.type);

      smasToSave.push({
        type: OrderNumberType.SMA,
        value: sma,
        subType: originalType,
      });
    });

    saveBulkSMAs(smasToSave);
  };

  const handleAddSelected = async () => {
    if (selectedOrderNumbers.length === 0 && newSMAs.length === 0) {
      errorBox('Please select SMAs to add');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    try {
      const selectedSMAsToAdd: OrderNumber[] = [];

      newSMAs.forEach((sma) => {
        selectedSMAsToAdd.push(sma);
      });

      selectedOrderNumbers.forEach((sma) => {
        const originalType =
          newSMAs.find((n) => n.value === sma)?.subType ||
          mapNetworkNodeTypeToOrderNumberSubType(mappings[sma]?.at(0)?.type);

        selectedSMAsToAdd.push({
          type: OrderNumberType.SMA,
          value: sma,
          subType: originalType,
        });
      });

      // For each entity, fetch current SMAs and add selected ones (without duplicates)
      const scansResults = await Promise.allSettled(
        scansIds.map(async (entityId) => {
          const currentSMAs = await getScanOrderNumbers(entityId);
          const combinedSMAs = dedupeOrderNumbers([...currentSMAs, ...selectedSMAsToAdd]);
          await saveSMA({
            orderNumbers: combinedSMAs,
            entityId,
            entityType: 'scans',
          });
          const textSmaList = combinedSMAs.map((sma) => sma.value).join(', ');
          const msg = `SMA numbers ${textSmaList} assigned to scan ${entityId} successfully.`;
          successBox(msg);
        }),
      );

      const photosResults = await Promise.allSettled(
        photosIds.map(async (entityId) => {
          const currentSMAs = getPhotoOrderNumbers(entityId);
          const combinedSMAs = dedupeOrderNumbers([...currentSMAs, ...selectedSMAsToAdd]);
          await saveSMA({
            orderNumbers: combinedSMAs,
            entityId,
            entityType: 'photos',
          });
          const textSmaList = combinedSMAs.map((sma) => sma.value).join(', ');
          const msg = `SMA numbers ${textSmaList} assigned to photo ${entityId} successfully.`;
          successBox(msg);
        }),
      );

      handleAssignmentResults(scansResults, photosResults);
    } catch (_error) {
      errorBox('Failed to add selected SMAs');
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedOrderNumbers.length === 0) {
      errorBox('Please select SMAs to delete');
      return;
    }

    setIsLoading(true);
    setProgress(0);
    try {
      // For each entity, fetch current SMAs and remove selected ones
      const scansResults = await Promise.allSettled(
        scansIds.map(async (entityId) => {
          const currentSMAs = await getScanOrderNumbers(entityId);
          const filteredSMAs = currentSMAs.filter(
            (sma) => !selectedOrderNumbers.includes(sma.value),
          );
          await saveSMA({
            orderNumbers: filteredSMAs.length > 0 ? filteredSMAs : null,
            entityId,
            entityType: 'scans',
          });
          const textSmaList = selectedOrderNumbers.join(', ');
          const updateMsg = `SMA numbers ${textSmaList} deleted from scan ${entityId} successfully.`;
          successBox(updateMsg);
        }),
      );

      const photosResults = await Promise.allSettled(
        photosIds.map(async (entityId) => {
          const currentSMAs = getPhotoOrderNumbers(entityId);
          const filteredSMAs = currentSMAs.filter(
            (sma) => !selectedOrderNumbers.includes(sma.value),
          );
          await saveSMA({
            orderNumbers: filteredSMAs.length > 0 ? filteredSMAs : null,
            entityId,
            entityType: 'photos',
          });
          const textSmaList = selectedOrderNumbers.join(', ');
          const updateMsg = `SMA numbers ${textSmaList} deleted from photo ${entityId} successfully.`;
          successBox(updateMsg);
        }),
      );

      handleAssignmentResults(scansResults, photosResults);
    } catch (_error) {
      errorBox('Failed to delete selected SMAs');
    } finally {
      setIsLoading(false);
      setProgress(0);
    }
  };

  const handleAddSMAClick = () => {
    setShowNewInput(true);
  };

  const handleCopyIdsToClipboard = async () => {
    const payload = buildCopiedIdsPayload(scansIds, photosIds);
    if (!payload) return;

    try {
      await navigator.clipboard.writeText(payload);
      successBox('IDs copied to clipboard');
    } catch {
      errorBox('Failed to copy IDs to clipboard');
    }
  };

  const handleSelectAllFailedAssets = () => {
    const photoErrors = assignmentErrors.filter((error) => error.asset === 'photo');
    const scanErrors = assignmentErrors.filter((error) => error.asset === 'scan');
    reduceSelectedPhotos(photoErrors.map((error) => error.id));
    reduceCheckedScans(scanErrors.map((error) => error.id));
    setAssignmentErrors([]);
  };

  const totalSelectedSMAs = selectedOrderNumbers.length;

  const isTelekomProject = project?.exportTarget === ExportTarget.Telekom;

  return (
    <>
      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Button
            onClick={() => {
              uncheckAllScans();
              setSelectedPhotos([]);
              setPhotoSelectionBoundingBox({ minX: 0, minY: 0, maxX: 0, maxY: 0 });
            }}
            variant="outlined"
            color="error"
            sx={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            Reset
          </Button>
          <Button
            onClick={handleCopyIdsToClipboard}
            variant="outlined"
            disabled={scansIds.length === 0 && photosIds.length === 0}
            sx={{ whiteSpace: 'nowrap', flex: 1 }}
          >
            Copy IDs to clipboard
          </Button>
        </Box>
        {isTelekomProject && (
          <Button
            onClick={() => setOpen(true)}
            variant="contained"
            sx={{ whiteSpace: 'nowrap', width: '100%' }}
          >
            SMA Bulk Assignment
          </Button>
        )}
      </Box>
      {isTelekomProject && (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          PaperProps={{
            elevation: 11,
            sx: { minWidth: 800 },
          }}
        >
          <DialogTitle>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box component="div">{title}</Box>
              {progress > 0 && (
                <Typography variant="body1" color="text.secondary">
                  Updating {progress} / {scansIds.length + photosIds.length}
                </Typography>
              )}
              <IconButton
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                  position: 'absolute',
                  right: theme.spacing(1),
                  top: theme.spacing(1),
                })}
                size="large"
              >
                <XOutline fontSize={24} />
              </IconButton>
            </Stack>
          </DialogTitle>
          {progress > 0 && (
            <LinearProgress
              value={Math.round((progress / Math.max(scansIds.length + photosIds.length, 1)) * 100)}
              variant="determinate"
            />
          )}
          <DialogContent>
            <Stack>
              <FormLabel id="sma-checkboxes">SMAs</FormLabel>

              {showNewInput && (
                <AddSMADialog
                  open={open}
                  onClose={() => {
                    setShowNewInput(false);
                  }}
                  onAddSMA={handleNewSMA}
                  selectedSMAs={newSMAs}
                  onRemoveSelectedSMA={(sma) => {
                    setNewSMAs((prev) => prev.filter((item) => item.value !== sma.value));
                    setSelectedOrderNumbers((prev) => prev.filter((item) => item !== sma.value));
                  }}
                />
              )}

              {!showNewInput && (
                <Box
                  component="div"
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                  }}
                >
                  {allSMAs.length === 0 && (
                    <Typography variant="body1" color="text.secondary">
                      {isLoading ? 'Loading SMAs...' : 'No SMAs found. Please add SMA number.'}
                    </Typography>
                  )}
                  {allSMAs.map((sma) => {
                    const closed = isOrderNumberClosed(sma);
                    return (
                      <FormControlLabel
                        key={sma.value}
                        disabled={closed}
                        control={
                          <Checkbox
                            disabled={closed}
                            checked={selectedOrderNumbers.includes(sma.value)}
                            onChange={() => handleCheckboxChange(sma.value)}
                          />
                        }
                        label={`${getSmaLabel(sma.value)}${closed ? ' (Closed)' : ''}`}
                        sx={{
                          ...(closed && {
                            color: 'text.disabled',
                            opacity: 0.6,
                          }),
                        }}
                      />
                    );
                  })}
                  <Button
                    onClick={handleAddSMAClick}
                    color="primary"
                    variant="outlined"
                    disabled={isLoading || showNewInput}
                  >
                    Add SMA
                  </Button>
                </Box>
              )}
            </Stack>
            {totalSelectedSMAs > 1 && !showNewInput && (
              <Alert severity="warning" sx={{ my: 2, flexGrow: 1 }}>
                {totalSelectedSMAs} SMAs are selected, they will be assigned to the selected assets
              </Alert>
            )}
            {assignmentErrors.length > 0 && (
              <Box component="div">
                <Alert severity="warning" sx={{ my: 2, flexGrow: 1 }}>
                  {assignmentErrors.length} assets failed to assign SMA numbers:
                  <ul>
                    {assignmentErrors.map((error) => (
                      <li key={error.id}>
                        {error.asset} {error.id}
                      </li>
                    ))}
                  </ul>
                </Alert>
                <Button color="primary" variant="outlined" onClick={handleSelectAllFailedAssets}>
                  Retry Bulk Assignment for failed assets
                </Button>
              </Box>
            )}
          </DialogContent>

          {!showNewInput && assignmentErrors.length === 0 && (
            <DialogActions sx={{ padding: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                onClick={handleDeleteSelected}
                color="error"
                variant="contained"
                disabled={isLoading || selectedOrderNumbers.length === 0}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ mr: { sm: 'auto' } }}
              >
                Delete selected
              </Button>

              <Box sx={{ display: 'flex', gap: 1, ml: { sm: 'auto' } }}>
                <Button
                  onClick={handleAddSelected}
                  color="primary"
                  variant="outlined"
                  disabled={isLoading || selectedOrderNumbers.length === 0}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  Add selected
                </Button>
                <Button
                  onClick={handleOverrideWithSelected}
                  color="primary"
                  variant="contained"
                  disabled={isLoading || selectedOrderNumbers.length === 0}
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  Override with selected
                </Button>
              </Box>
            </DialogActions>
          )}
        </Dialog>
      )}
    </>
  );
};

export default SMABulkDialog;
